import { useState, useEffect } from "react";
import { ConfigPanel } from "./components/ConfigPanel";
import { OAuthLogin } from "./components/OAuthLogin";
import { LoadingScreen } from "./components/LoadingScreen";
import { ErrorScreen } from "./components/ErrorScreen";
import { getWixInstanceData, resizeWidget } from "./api";
import type { WixWidgetSettings } from "./types";

type AppState = "loading" | "login" | "config" | "error";

export function App() {
  const [appState, setAppState] = useState<AppState>("loading");
  const [settings, setSettings] = useState<WixWidgetSettings | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    initApp();
  }, []);

  useEffect(() => {
    resizeWidget(400, appState === "config" ? 600 : 480);
  }, [appState]);

  async function initApp() {
    try {
      const instanceData = getWixInstanceData();
      if (!instanceData) {
        setError("Could not connect to Wix. Please refresh the page.");
        setAppState("error");
        return;
      }
      // Always start with the login screen — after login the /connect endpoint
      // creates the website and sets the embed script param automatically.
      setAppState("login");
    } catch (err) {
      console.error("[BubblaV] App init error:", err);
      setError(err instanceof Error ? err.message : "Failed to initialize app");
      setAppState("error");
    }
  }

  function handleConnected(websiteId: string) {
    const instanceData = getWixInstanceData();
    setSettings({
      websiteId,
      botName: "Assistant",
      websiteName: instanceData?.siteName || "Wix Site",
      desktopPosition: "bottom-right",
      mobilePosition: "bottom-right",
      theme: "light",
    });
    setAppState("config");
  }

  if (appState === "loading") return <LoadingScreen />;
  if (appState === "error") return <ErrorScreen message={error || "An error occurred"} />;
  if (appState === "login") return <OAuthLogin onComplete={handleConnected} />;
  if (appState === "config" && settings) {
    return (
      <ConfigPanel
        settings={settings}
        onSettingsChange={setSettings}
        onClose={() => {
          // @ts-ignore
          window.Wix?.closeWindow?.();
        }}
      />
    );
  }

  return <LoadingScreen />;
}
